import PropTypes from 'prop-types';
import React from 'react';
import { FormattedMessage } from 'react-intl';
import { makeStyles } from '@material-ui/core/styles';
import { Typography } from '@material-ui/core';
import {
  AllocationInput,
  AllocationPeriodTabs,
  EntirePeriod,
  ProrationNote,
  TotalHoursLabel,
  ALLOCATION_PERIOD
} from './components';
import { usePeriodAllocationTotal } from './hooks';

export const MAX_HOURS_PER_WEEK = 168; // 7d * 24h
export const MAX_HOURS_PER_MONTH = 744; // 31d * 24h

const useStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    flexDirection: 'column'
  },
  header: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    padding: theme.spacing(1, 2, 0),
    gap: theme.spacing(2),
    borderBottom: `1px solid ${theme.palette.divider}`
  },
  label: {
    ...theme.typography.body2,
    color: theme.palette.text.secondary,
    width: theme.spacing(16),
    flexShrink: 0
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.5),
    padding: theme.spacing(2, 2, 1, 2)
  },
  row: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: theme.spacing(2)
  },
  rowLabel: {
    ...theme.typography.body2,
    color: theme.palette.text.secondary,
    width: theme.spacing(16),
    flexShrink: 0,
    paddingTop: theme.spacing(0.75)
  },
  totalLabelWrapper: {
    paddingTop: theme.spacing(0.75)
  }
}));

export const getHoursValueByPeriod = ({
  selectedAllocationPeriod,
  hoursForEntirePeriod,
  hoursPerMonth,
  hoursPerWeek
}) => {
  switch (selectedAllocationPeriod) {
    case ALLOCATION_PERIOD.MONTH:
      return hoursPerMonth;
    case ALLOCATION_PERIOD.WEEK:
      return hoursPerWeek;
    case ALLOCATION_PERIOD.ENTIRE_PERIOD:
    default:
      return hoursForEntirePeriod;
  }
};

const AllocationByPeriod = ({
  dateRange,
  selectedAllocationPeriod,
  hoursForEntirePeriod,
  percentageForEntirePeriod,
  hoursPerMonth,
  hoursPerWeek,
  onHoursValueChange,
  onPercentageValueChange,
  onSelectedAllocationPeriodChange,
  error,
  entirePeriodDisabled
}) => {
  const classes = useStyles();

  const hoursValue = getHoursValueByPeriod({
    selectedAllocationPeriod,
    hoursForEntirePeriod,
    hoursPerMonth,
    hoursPerWeek
  });

  const { totalHours, showProrationNote } = usePeriodAllocationTotal({
    ...dateRange,
    hoursPerPeriod: hoursValue,
    allocationPeriod: selectedAllocationPeriod
  });

  return (
    <div className={classes.root}>
      <div className={classes.header}>
        <Typography className={classes.label}>
          <FormattedMessage id="allocationByPeriod.label" />
        </Typography>
        <AllocationPeriodTabs
          selectedAllocationPeriod={selectedAllocationPeriod}
          onSelectedAllocationPeriodChange={onSelectedAllocationPeriodChange}
        />
      </div>
      <div className={classes.content}>
        {selectedAllocationPeriod === ALLOCATION_PERIOD.ENTIRE_PERIOD ? (
          <EntirePeriod
            dateRange={dateRange}
            selectedAllocationPeriod={selectedAllocationPeriod}
            hoursValue={hoursForEntirePeriod}
            onHoursValueChange={onHoursValueChange}
            percentageValue={percentageForEntirePeriod}
            onPercentageValueChange={onPercentageValueChange}
            error={error}
            disabled={entirePeriodDisabled}
          />
        ) : (
          <>
            <div className={classes.row}>
              <Typography className={classes.rowLabel}>
                <FormattedMessage id="allocationByPeriod.allocate" />
              </Typography>
              <AllocationInput
                value={hoursValue}
                selectedAllocationPeriod={selectedAllocationPeriod}
                maxValue={
                  selectedAllocationPeriod === ALLOCATION_PERIOD.MONTH
                    ? MAX_HOURS_PER_MONTH
                    : MAX_HOURS_PER_WEEK
                }
                onChange={onHoursValueChange}
                error={Boolean(error)}
                helperText={error}
              />
              <div className={classes.totalLabelWrapper}>
                <TotalHoursLabel totalHours={totalHours} />
              </div>
            </div>
            {showProrationNote && (
              <ProrationNote allocationPeriod={selectedAllocationPeriod} />
            )}
          </>
        )}
      </div>
    </div>
  );
};

AllocationByPeriod.propTypes = {
  dateRange: PropTypes.shape({
    startDate: PropTypes.object.isRequired,
    endDate: PropTypes.object.isRequired
  }).isRequired,
  selectedAllocationPeriod: PropTypes.oneOf(Object.values(ALLOCATION_PERIOD))
    .isRequired,
  hoursForEntirePeriod: PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.string
  ]).isRequired,
  percentageForEntirePeriod: PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.string
  ]).isRequired,
  hoursPerMonth: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
    .isRequired,
  hoursPerWeek: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
    .isRequired,
  onHoursValueChange: PropTypes.func.isRequired,
  onPercentageValueChange: PropTypes.func.isRequired,
  onSelectedAllocationPeriodChange: PropTypes.func.isRequired,
  error: PropTypes.string,
  entirePeriodDisabled: PropTypes.bool
};

export default AllocationByPeriod;
